import SelectMenuActionButton from "components/shared/selectMenuActionButton";
import { featureModuleTypesEnum } from "helpers/enums";
import { HiddenDataWrapper } from "helpers/hiddenData";
import { ymEvent } from "helpers/system";
import { requiredField } from "helpers/validators";
import useIsMounted from "hooks/useIsMounted";
import policy from "policies";
import React, { Fragment, useEffect, useState } from "react";
import { components } from "react-select";
import PaymentFields from "../../paymentFields";

const HIRED_PARK_ITEMS_CONFIG = {
    hiredCarId: {
        book: "trucks",
        buttonText: "Создать наёмный ТС",
        modalName: "hiredCarForm",
        title: "Создание наёмного ТС",
    },
    hiredTrailerId: {
        book: "trailers",
        buttonText: "Создать наёмный прицеп",
        modalName: "hiredTrailerForm",
        title: "Создание наёмного прицепа",
    },
    hiredDriverId: {
        book: "drivers",
        buttonText: "Создать наёмного водителя",
        modalName: "hiredDriverForm",
        title: "Создание наёмного водителя",
    },
};

const HiredParkMenuList = (props) => {
    const {
        children,
        selectProps: {
            canCreateHiredParkItem,
            createHiredParkItemText,
            onCreateHiredParkItem,
            onToggleShowAllHiredPark,
            showAllHiredPark,
        },
    } = props;
    return (
        <>
            <components.MenuList {...props}>{children}</components.MenuList>
            <div className="text-center border-top">
                <SelectMenuActionButton onAction={onToggleShowAllHiredPark}>
                    {showAllHiredPark ? "Показывать по перевозчику" : "Показать все"}
                </SelectMenuActionButton>
            </div>
            {canCreateHiredParkItem && (
                <div className="text-center border-top">
                    <SelectMenuActionButton onAction={onCreateHiredParkItem}>
                        {createHiredParkItemText}
                    </SelectMenuActionButton>
                </div>
            )}
        </>
    );
};

const HiredCar = ({
    config,
    renderField,
    renderCheckbox,
    renderSelect,
    renderDateField,
    hasHiredCarValue,
    onChange,
    hiredCarSettings,
    modal,
    organization,
    profile,
    roles,
}) => {
    const isMounted = useIsMounted();
    const [showAllHiredPark, setShowAllHiredPark] = useState({});

    const {
        hasHiredCar,
        hiredCarCounterpartyId,
        hiredCarPrice,
        hiredCarPriceWithoutVatOnTop,
        hiredCarCompletedAt,
        hiredCarNdsTypeId,
        hiredCarPaymentTypeId,
        hiredCarInvoiceTriggerType,
        hiredCarPaymentPeriodType,
        hiredCarPaymentPeriodInDays,
        isHiredCarVatTop,
        hiredCarId,
        hiredTrailerId,
        hiredDriverId,
    } = config;
    const priceConfig = hiredCarSettings.isHiredCarVatTop ? hiredCarPriceWithoutVatOnTop : hiredCarPrice;
    const priceValue = hiredCarSettings.isHiredCarVatTop
        ? hiredCarSettings.hiredCarPriceWithoutVatOnTop
        : hiredCarSettings.hiredCarPrice;
    const canCreateHiredParkItems = React.useMemo(() => {
        return Object.entries(HIRED_PARK_ITEMS_CONFIG).reduce((acc, [fieldName, item]) => {
            acc[fieldName] = policy("user", {
                user: {
                    canEditAll: !organization.noAccessToEditBooks,
                    intBooksEditPermissionsFlags: organization.intBooksEditPermissionsFlags,
                    roles,
                },
                record: {},
                options: { book: item.book },
            }).canEditBook();

            return acc;
        }, {});
    }, [organization, roles]);

    useEffect(() => {
        if (!isMounted) {
            return;
        }
        if (hasHiredCarValue) {
            ymEvent("HiredVehicle'sActivated");
        }
    }, [hasHiredCarValue]);

    useEffect(() => {
        setShowAllHiredPark({});
    }, [hiredCarSettings.hiredCarCounterpartyId]);

    const getHiredParkUrl = (url, fieldName) => (params) =>
        url(
            showAllHiredPark[fieldName] || !hiredCarSettings.hiredCarCounterpartyId
                ? params
                : {
                      ...params,
                      counterpartyIds: [hiredCarSettings.hiredCarCounterpartyId],
                  },
        );

    const getHiredParkSelectProps = (fieldName) => ({
        canCreateHiredParkItem: canCreateHiredParkItems[fieldName],
        createHiredParkItemText: HIRED_PARK_ITEMS_CONFIG[fieldName].buttonText,
        dataKey: `${hiredCarSettings.hiredCarCounterpartyId}_${fieldName}_${showAllHiredPark[fieldName]}`,
        onCreateHiredParkItem: () => {
            const item = HIRED_PARK_ITEMS_CONFIG[fieldName];
            modal({
                name: item.modalName,
                title: item.title,
                windowMod: "sm",
                data: { counterpartyIds: [hiredCarSettings.hiredCarCounterpartyId] },
                onSuccess: (createdItem) => {
                    if (!createdItem?.id) {
                        return;
                    }
                    onChange({
                        name: fieldName,
                        value: createdItem.id,
                        option: createdItem,
                    });
                },
            });
        },
        onToggleShowAllHiredPark: () => setShowAllHiredPark((value) => ({ ...value, [fieldName]: !value[fieldName] })),
        showAllHiredPark: showAllHiredPark[fieldName],
    });

    return (
        <>
            {renderCheckbox({ ...hasHiredCar, fieldWrapper: "col-sm-8 col-md-5" })}
            {hasHiredCarValue && (
                <div className="row">
                    <div className="offset-sm-4 offset-md-3 col-sm-8 col-md-5">
                        <div className="card card-body mb-3">
                            {renderSelect({
                                ...hiredCarCounterpartyId,
                                modal,
                                onChange: (data) => {
                                    onChange(data);
                                    onChange({ name: "hiredCarId", value: null });
                                    onChange({ name: "hiredTrailerId", value: null });
                                    onChange({ name: "hiredDriverId", value: null });
                                    setShowAllHiredPark({});
                                },
                                organization,
                                profile,
                                roles,
                                value: hiredCarSettings.hiredCarCounterpartyId,
                                labelWrapper: "col-12 col-form-label col-form-label-sm text-muted",
                                fieldWrapper: "col-12",
                                validators: [requiredField],
                            })}
                            <HiddenDataWrapper>
                                {renderField({
                                    ...priceConfig,
                                    onChange,
                                    value: priceValue,
                                    labelWrapper: "col-12 col-form-label col-form-label-sm text-muted",
                                    fieldWrapper: "col-12",
                                    validators: [requiredField],
                                })}
                            </HiddenDataWrapper>
                            {renderSelect({
                                ...hiredCarPaymentTypeId,
                                onChange,
                                value: hiredCarSettings.hiredCarPaymentTypeId,
                                labelWrapper: "col-12 col-form-label col-form-label-sm text-muted",
                                fieldWrapper: "col-12",
                            })}
                            {renderSelect({
                                ...hiredCarNdsTypeId,
                                onChange,
                                value: hiredCarSettings.hiredCarNdsTypeId,
                                labelWrapper: "col-12 col-form-label col-form-label-sm text-muted",
                                fieldWrapper: "col-12",
                            })}
                            {renderCheckbox({
                                ...isHiredCarVatTop,
                                fieldBeforeLabel: true,
                                onChange,
                                value: hiredCarSettings.isHiredCarVatTop,
                                labelWrapper: "col-auto col-form-label col-form-label-sm pl-0",
                                fieldWrapper: "col-auto",
                            })}
                            {renderSelect({
                                ...hiredCarPaymentPeriodType,
                                onChange,
                                value: hiredCarSettings.hiredCarPaymentPeriodType,
                                labelWrapper: "col-12 col-form-label col-form-label-sm text-muted",
                                fieldWrapper: "col-12",
                            })}
                            {renderField({
                                ...hiredCarPaymentPeriodInDays,
                                onChange,
                                value: hiredCarSettings.hiredCarPaymentPeriodInDays,
                                labelWrapper: "col-12 col-form-label col-form-label-sm text-muted",
                                fieldWrapper: "col-12",
                            })}
                            {renderSelect({
                                ...hiredCarInvoiceTriggerType,
                                onChange,
                                value: hiredCarSettings.hiredCarInvoiceTriggerType,
                                labelWrapper: "col-12 col-form-label col-form-label-sm text-muted",
                                fieldWrapper: "col-12",
                            })}
                            {profile.availableFeatureModules.includes(featureModuleTypesEnum.AccountsReceivable) && (
                                <>
                                    <strong className="mb-2">Расчёты с перевозчиком</strong>
                                    <PaymentFields
                                        payment={hiredCarSettings.hiredCarPayment}
                                        isAccountsPayable
                                        onChange={(value) => onChange({ name: "hiredCarPayment", value })}
                                        renderDateField={renderDateField}
                                        renderField={renderField}
                                        renderSelect={renderSelect}
                                    />
                                </>
                            )}
                            {renderDateField({
                                ...hiredCarCompletedAt,
                                onChange,
                                value: hiredCarSettings.hiredCarCompletedAt,
                                labelWrapper: "col-12 col-form-label col-form-label-sm text-muted",
                                fieldWrapper: "col-12",
                            })}
                            <Fragment key={hiredCarSettings.hiredCarCounterpartyId}>
                                {renderSelect({
                                    ...hiredCarId,
                                    ...getHiredParkSelectProps("hiredCarId"),
                                    components: { ...hiredCarId.components, MenuList: HiredParkMenuList },
                                    url: getHiredParkUrl(hiredCarId.url, "hiredCarId"),
                                    onChange,
                                    disabled: !hiredCarSettings.hiredCarCounterpartyId,
                                    value: hiredCarSettings.hiredCarId,
                                    labelWrapper: "col-12 col-form-label col-form-label-sm text-muted",
                                    fieldWrapper: "col-12",
                                })}
                                {renderSelect({
                                    ...hiredTrailerId,
                                    ...getHiredParkSelectProps("hiredTrailerId"),
                                    components: { ...hiredTrailerId.components, MenuList: HiredParkMenuList },
                                    url: getHiredParkUrl(hiredTrailerId.url, "hiredTrailerId"),
                                    onChange,
                                    disabled: !hiredCarSettings.hiredCarCounterpartyId,
                                    value: hiredCarSettings.hiredTrailerId,
                                    labelWrapper: "col-12 col-form-label col-form-label-sm text-muted",
                                    fieldWrapper: "col-12",
                                })}
                                {renderSelect({
                                    ...hiredDriverId,
                                    ...getHiredParkSelectProps("hiredDriverId"),
                                    components: { ...hiredDriverId.components, MenuList: HiredParkMenuList },
                                    url: getHiredParkUrl(hiredDriverId.url, "hiredDriverId"),
                                    onChange,
                                    disabled: !hiredCarSettings.hiredCarCounterpartyId,
                                    value: hiredCarSettings.hiredDriverId,
                                    labelWrapper: "col-12 col-form-label col-form-label-sm text-muted",
                                    fieldWrapper: "col-12",
                                })}
                            </Fragment>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default HiredCar;
